import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, ShopOutlined, DatabaseOutlined } from '@ant-design/icons';
import axios from '../../utils/axiosConfig';
import './DatabaseManagement.css';

const { TabPane } = Tabs;
const { Option } = Select;

function DatabaseManagement() {
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [geotabAccounts, setGeotabAccounts] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [whitelabelAgencies, setWhitelabelAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState(''); // 'client', 'enterprise', 'geotab'
  const [editingRecord, setEditingRecord] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [form] = Form.useForm();

  const authHeader = useCallback(() => {
    const token = sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  // Fetch Clients (users with role 'client')
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const hdr = authHeader();
      const { data } = await axios.get('/api/users', hdr ? { headers: hdr } : undefined);

      // Handle different possible data structures
      let usersData = [];
      if (Array.isArray(data)) {
        usersData = data;
      } else if (data && Array.isArray(data.items)) {
        usersData = data.items;
      } else if (data && Array.isArray(data.users)) {
        usersData = data.users;
      } else if (data && Array.isArray(data.data)) {
        usersData = data.data;
      } else if (data && typeof data === 'object') {
        const arrayProps = Object.values(data).filter((val) => Array.isArray(val));
        if (arrayProps.length > 0) usersData = arrayProps[0];
      }

      // Filter only clients (role = 'client')
      const clientsData = usersData.filter(user => user.role === 'client');
      setClients(clientsData);
    } catch (error) {
      message.error('Failed to fetch clients: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const hdr = authHeader();
      const { data } = await axios.get('/api/workspaces?limit=500', hdr ? { headers: hdr } : undefined);
      let items = [];
      if (Array.isArray(data)) items = data;
      else if (data && Array.isArray(data.items)) items = data.items;
      else if (data && Array.isArray(data.data)) items = data.data;
      setWorkspaces(items);
    } catch (error) {
      setWorkspaces([]);
    }
  }, [authHeader]);

  const fetchAgenciesLookup = useCallback(async () => {
    try {
      const hdr = authHeader();
      const { data } = await axios.get('/api/agencies?limit=500', hdr ? { headers: hdr } : undefined);
      let items = [];
      if (Array.isArray(data)) items = data;
      else if (data && Array.isArray(data.items)) items = data.items;
      else if (data && Array.isArray(data.data)) items = data.data;
      setAgencies(items);
    } catch (error) {
      setAgencies([]);
    }
  }, [authHeader]);

  const fetchWhitelabelAgenciesLookup = useCallback(async () => {
    try {
      const hdr = authHeader();
      const { data } = await axios.get('/api/whitelabel-agencies?limit=500', hdr ? { headers: hdr } : undefined);
      let items = [];
      if (Array.isArray(data)) items = data;
      else if (data && Array.isArray(data.items)) items = data.items;
      else if (data && Array.isArray(data.data)) items = data.data;
      setWhitelabelAgencies(items);
    } catch (error) {
      setWhitelabelAgencies([]);
    }
  }, [authHeader]);

  // Fetch Enterprises (users with role 'agency')
  const fetchEnterprises = useCallback(async () => {
    setLoading(true);
    try {
      const hdr = authHeader();
      console.log('Fetching enterprises from /api/users (role=agency)...');
      const { data } = await axios.get('/api/users', hdr ? { headers: hdr } : undefined);
      console.log('Raw users data:', data);

      let usersData = [];
      if (Array.isArray(data)) {
        usersData = data;
      } else if (data && Array.isArray(data.items)) {
        usersData = data.items;
      } else if (data && Array.isArray(data.users)) {
        usersData = data.users;
      } else if (data && Array.isArray(data.data)) {
        usersData = data.data;
      }

      // Filter only enterprises (role = 'agency')
      const enterprisesData = usersData.filter(user => user.role === 'agency');
      console.log('Filtered enterprises data:', enterprisesData);
      console.log('Setting enterprises count:', enterprisesData.length);
      
      // Map user data to enterprise table format
      const mappedEnterprises = enterprisesData.map(user => ({
        id: user.id,
        name: user.full_name || user.email || `Enterprise #${user.id}`,
        owner_user_id: user.id, // Enterprise user is their own owner
        status: 'active', // Default status for enterprise users
        created_at: user.created_at,
        email: user.email
      }));
      
      setEnterprises(mappedEnterprises);
    } catch (error) {
      console.error('fetchEnterprises error:', error);
      message.error('Failed to fetch enterprises: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  // Fetch Geotab Accounts
  const fetchGeotabAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const hdr = authHeader();
      if (!hdr) {
        message.error('Missing token');
        setGeotabAccounts([]);
        return;
      }

      const { data } = await axios.get('/api/integrations/geotab/accounts', { headers: hdr });

      let accountsData = [];
      if (Array.isArray(data)) {
        accountsData = data;
      } else if (data && Array.isArray(data.accounts)) {
        accountsData = data.accounts;
      } else if (data && Array.isArray(data.items)) {
        accountsData = data.items;
      } else if (data && Array.isArray(data.data)) {
        accountsData = data.data;
      }

      console.log('Geotab accounts fetched:', accountsData);
      setGeotabAccounts(accountsData);
    } catch (error) {
      message.error('Failed to fetch Geotab accounts: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchClients();
    fetchEnterprises();
    fetchGeotabAccounts();
    fetchWorkspaces();
    fetchAgenciesLookup();
    fetchWhitelabelAgenciesLookup();
  }, [fetchClients, fetchEnterprises, fetchGeotabAccounts, fetchWorkspaces, fetchAgenciesLookup, fetchWhitelabelAgenciesLookup]);

  const agencyIdToName = useCallback(() => {
    const m = new Map();
    agencies.forEach((a) => m.set(a.id, a.name));
    return m;
  }, [agencies]);

  const whitelabelIdToName = useCallback(() => {
    const m = new Map();
    whitelabelAgencies.forEach((a) => m.set(a.id, a.name));
    return m;
  }, [whitelabelAgencies]);

  const getUserLocationsLabel = useCallback((u) => {
    const role = String(u?.role || '').toLowerCase();
    if (role === 'whitelabel_agency' || u?.whitelabel_agency_id) {
      const name = whitelabelIdToName().get(u.whitelabel_agency_id);
      return name ? `Reseller: ${name}` : 'Reseller';
    }

    if (role === 'agency' || u?.agency_id) {
      const list = workspaces.filter((w) => w.agency_id === u.agency_id);
      if (list.length === 0) {
        const name = agencyIdToName().get(u.agency_id);
        return name ? `Enterprise: ${name}` : 'No locations';
      }
      const names = list.map((w) => w.name).filter(Boolean);
      return names.length ? `${names.length} locations: ${names.join(', ')}` : `${list.length} locations`;
    }

    if (role === 'client') {
      const list = workspaces.filter((w) => w.client_id === u.id);
      return list[0]?.name || 'No location';
    }

    return '—';
  }, [agencyIdToName, whitelabelIdToName, workspaces]);

  // Open modal for add/edit
  const openModal = (type, record = null) => {
    setModalType(type);
    setEditingRecord(record);
    setModalVisible(true);
    
    if (record) {
      // Pre-populate form with existing data
      const formData = { ...record };
      
      // Split full_name back into first_name and last_name for editing
      if (record.full_name) {
        const nameParts = record.full_name.split(' ');
        formData.first_name = nameParts[0] || '';
        formData.last_name = nameParts.slice(1).join(' ') || '';
      }
      
      // Find all currently assigned Geotab accounts
      const assignedGeotabs = geotabAccounts.filter(acc => Number(acc.user_id) === Number(record.id));
      if (assignedGeotabs.length > 0) {
        formData.geotab_databases = assignedGeotabs.map(acc => acc.id);
      }
      
      form.setFieldsValue(formData);
    } else {
      form.resetFields();
    }
  };

  // Close modal
  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      if (modalType === 'client') {
        const authHeaders = authHeader();
        
        // Combine first_name and last_name into full_name for the backend
        const userData = {
          ...values,
          full_name: `${values.first_name} ${values.last_name}`.trim(),
          role: 'client' // Ensure role is set to client
        };
        delete userData.first_name;
        delete userData.last_name;
        
        console.log('Sending client data to backend:', { ...userData, password: userData.password ? '[REDACTED]' : undefined });
        
        if (editingRecord) {
          // Handle password reset and Geotab assignment for existing clients
          const updateData = { ...userData };
          if (!updateData.password) {
            delete updateData.password; // Don't send empty password
          }
          delete updateData.geotab_databases; // Handle separately
          
          await axios.put(`/api/users/${editingRecord.id}`, updateData, authHeaders ? { headers: authHeaders } : undefined);
          message.success('Client updated successfully');
          
          // Handle Geotab database assignments (multiple)
          if (values.geotab_databases && values.geotab_databases.length > 0) {
            try {
              // For clients, find their agency owner and assign to the owner
              const clientRes = await axios.get(`/api/clients?user_id=${editingRecord.id}`, authHeaders ? { headers: authHeaders } : undefined);
              const clientData = Array.isArray(clientRes.data) ? clientRes.data[0] : (clientRes.data?.items?.[0] || null);
              
              let targetUserId = editingRecord.id; // Default to client user
              
              if (clientData?.agency_id) {
                // Find agency owner
                const agencyRes = await axios.get(`/api/agencies/${clientData.agency_id}`, authHeaders ? { headers: authHeaders } : undefined);
                if (agencyRes.data?.owner_user_id) {
                  targetUserId = agencyRes.data.owner_user_id;
                  console.log(`Assigning Geotab to agency owner (user_id=${targetUserId}) instead of client (user_id=${editingRecord.id})`);
                }
              }
              
              // First, unassign all previously assigned databases for this user
              const previouslyAssigned = geotabAccounts.filter(acc => Number(acc.user_id) === Number(targetUserId));
              for (const acc of previouslyAssigned) {
                if (!values.geotab_databases.includes(acc.id)) {
                  await axios.put(`/api/integrations/geotab/accounts/${acc.id}`, 
                    { user_id: null }, 
                    authHeaders ? { headers: authHeaders } : undefined
                  );
                }
              }
              
              // Assign all selected databases
              for (const dbId of values.geotab_databases) {
                await axios.put(`/api/integrations/geotab/accounts/${dbId}`, 
                  { user_id: targetUserId }, 
                  authHeaders ? { headers: authHeaders } : undefined
                );
              }
              message.success(`${values.geotab_databases.length} Geotab database(s) assigned successfully`);
            } catch (geotabErr) {
              message.warning('Client updated but Geotab assignment failed: ' + (geotabErr.response?.data?.error || geotabErr.message));
            }
          }
        } else {
          // Ensure password is set for new clients
          if (!userData.password) {
            console.error('Password is missing from userData');
            message.error('Password is required for new clients');
            return;
          }
          console.log('Creating new client with password');
          const response = await axios.post('/api/users', userData, authHeaders ? { headers: authHeaders } : undefined);
          message.success('Client created successfully');
          
          // Handle Geotab database assignments for new clients (multiple)
          if (values.geotab_databases && values.geotab_databases.length > 0 && response.data?.id) {
            try {
              // For clients, find their agency owner and assign to the owner
              const clientRes = await axios.get(`/api/clients?user_id=${response.data.id}`, authHeaders ? { headers: authHeaders } : undefined);
              const clientData = Array.isArray(clientRes.data) ? clientRes.data[0] : (clientRes.data?.items?.[0] || null);
              
              let targetUserId = response.data.id; // Default to client user
              
              if (clientData?.agency_id) {
                // Find agency owner
                const agencyRes = await axios.get(`/api/agencies/${clientData.agency_id}`, authHeaders ? { headers: authHeaders } : undefined);
                if (agencyRes.data?.owner_user_id) {
                  targetUserId = agencyRes.data.owner_user_id;
                  console.log(`Assigning Geotab to agency owner (user_id=${targetUserId}) instead of new client (user_id=${response.data.id})`);
                }
              }
              
              // Assign all selected databases
              for (const dbId of values.geotab_databases) {
                await axios.put(`/api/integrations/geotab/accounts/${dbId}`, 
                  { user_id: targetUserId }, 
                  authHeaders ? { headers: authHeaders } : undefined
                );
              }
              message.success(`${values.geotab_databases.length} Geotab database(s) assigned successfully`);
            } catch (geotabErr) {
              message.warning('Client created but Geotab assignment failed: ' + (geotabErr.response?.data?.error || geotabErr.message));
            }
          }
        }
        fetchClients();
        fetchGeotabAccounts(); // Refresh Geotab accounts to show assignments
      } else if (modalType === 'enterprise') {
        const authHeaders = authHeader();
        if (editingRecord) {
          // Handle password reset and Geotab assignment for existing enterprises
          const updateData = { ...values };
          if (!updateData.password) {
            delete updateData.password; // Don't send empty password
          }
          delete updateData.geotab_databases; // Handle separately
          
          await axios.put(`/api/users/${editingRecord.id}`, updateData, authHeaders ? { headers: authHeaders } : undefined);
          message.success('Enterprise updated successfully');
          
          // Handle Geotab database assignments (multiple)
          if (values.geotab_databases && values.geotab_databases.length > 0) {
            try {
              // First, unassign all previously assigned databases for this user
              const previouslyAssigned = geotabAccounts.filter(acc => Number(acc.user_id) === Number(editingRecord.id));
              for (const acc of previouslyAssigned) {
                if (!values.geotab_databases.includes(acc.id)) {
                  await axios.put(`/api/integrations/geotab/accounts/${acc.id}`, 
                    { user_id: null }, 
                    authHeaders ? { headers: authHeaders } : undefined
                  );
                }
              }
              
              // Assign all selected databases
              for (const dbId of values.geotab_databases) {
                await axios.put(`/api/integrations/geotab/accounts/${dbId}`, 
                  { user_id: editingRecord.id }, 
                  authHeaders ? { headers: authHeaders } : undefined
                );
              }
              message.success(`${values.geotab_databases.length} Geotab database(s) assigned successfully`);
            } catch (geotabErr) {
              message.warning('Enterprise updated but Geotab assignment failed: ' + (geotabErr.response?.data?.error || geotabErr.message));
            }
          }
        } else {
          // Enterprise creation - just create a user with role 'agency'
          const userData = {
            full_name: values.name,
            email: values.email,
            password: values.password,
            role: 'agency' // Enterprise owner role
          };
          
          console.log('Creating enterprise user...');
          const userResponse = await axios.post('/api/users', userData, authHeaders ? { headers: authHeaders } : undefined);
          console.log('Enterprise user created:', userResponse.data);
          
          // Handle Geotab database assignments to the enterprise user (multiple)
          if (values.geotab_databases && values.geotab_databases.length > 0) {
            console.log('Assigning Geotab databases to enterprise user...');
            for (const dbId of values.geotab_databases) {
              await axios.put(`/api/integrations/geotab/accounts/${dbId}`, 
                { user_id: userResponse.data.id }, 
                authHeaders ? { headers: authHeaders } : undefined
              );
            }
            console.log(`${values.geotab_databases.length} Geotab database(s) assigned`);
          }
          
          message.success('Enterprise user created successfully');
        }
        
        // Refresh enterprises list after creation or update
        await fetchEnterprises();
        await fetchGeotabAccounts();
      } else if (modalType === 'geotab') {
        const authHeaders = authHeader();
        if (editingRecord) {
          await axios.put(`/api/integrations/geotab/accounts/${editingRecord.id}`, values, { headers: authHeaders });
          message.success('Geotab account updated successfully');
        } else {
          await axios.post('/api/integrations/geotab/accounts', values, { headers: authHeaders });
          message.success('Geotab account created successfully');
        }
        fetchGeotabAccounts();
      } else if (modalType === 'assign-geotab') {
        const authHeaders = authHeader();
        
        // If existing account is selected, update it to assign to this user
        if (values.existing_account_id) {
          await axios.put(
            `/api/integrations/geotab/accounts/${values.existing_account_id}`, 
            { user_id: editingRecord.user_id },
            { headers: authHeaders }
          );
          message.success('Existing Geotab account assigned successfully');
        } else {
          // Create new account for this user
          const payload = {
            database: values.database,
            username: values.username,
            password: values.password,
            base_url: values.base_url || 'https://my.geotab.com',
            user_id: editingRecord.user_id
          };
          await axios.post('/api/integrations/geotab/accounts', payload, { headers: authHeaders });
          message.success('New Geotab database assigned successfully');
        }
        
        fetchGeotabAccounts();
        fetchClients();
      }
      closeModal();
    } catch (error) {
      message.error('Operation failed: ' + (error.response?.data?.error || error.message));
    }
  };

  // Delete record
  const handleDelete = async (type, id) => {
    try {
      if (type === 'client') {
        const authHeaders = authHeader();
        await axios.delete(`/api/users/${id}`, authHeaders ? { headers: authHeaders } : undefined);
        message.success('Client deleted successfully');
        fetchClients();
      } else if (type === 'enterprise') {
        const authHeaders = authHeader();
        await axios.delete(`/api/users/${id}`, authHeaders ? { headers: authHeaders } : undefined);
        message.success('Enterprise deleted successfully');
        fetchEnterprises();
      } else if (type === 'geotab') {
        const authHeaders = authHeader();
        await axios.delete(`/api/integrations/geotab/accounts/${id}`, { headers: authHeaders });
        message.success('Geotab account deleted successfully');
        fetchGeotabAccounts();
      }
    } catch (error) {
      message.error('Delete failed: ' + (error.response?.data?.error || error.message));
    }
  };

  
  // Test Geotab connection and get session ID
  const handleTestGeotabConnection = async () => {
    try {
      const values = await form.validateFields(['database', 'username', 'password', 'base_url']);
      
      setTestingConnection(true);
      const authHeaders = authHeader();
      
      const payload = {
        userName: values.username,
        password: values.password,
        database: values.database,
        base_url: values.base_url || 'https://my.geotab.com',
      };
      
      const { data } = await axios.post('/api/integrations/geotab/authenticate', payload, authHeaders ? { headers: authHeaders } : undefined);
      
      if (data?.credentials?.sessionId) {
        form.setFieldsValue({ session_id: data.credentials.sessionId });
        message.success('Successfully authenticated! Session ID retrieved.');
      } else {
        message.warning('Authentication succeeded but no session ID returned');
      }
    } catch (error) {
      console.error('Test connection error:', error);
      message.error('Authentication failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setTestingConnection(false);
    }
  };

  // Clients table columns
  const clientColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      key: 'role',
      width: 140,
      render: (_, record) => {
        const role = String(record?.role || '').toLowerCase();
        let label, color;
        
        if (role === 'superadmin') {
          label = 'Super Admin';
          color = 'red';
        } else if (role === 'admin') {
          label = 'Admin';
          color = 'orange';
        } else if (role === 'whitelabel_agency' || record?.whitelabel_agency_id) {
          label = 'Reseller';
          color = 'purple';
        } else if (role === 'agency' || record?.agency_id) {
          label = 'Enterprise';
          color = 'green';
        } else if (role === 'client') {
          label = 'Client';
          color = 'blue';
        } else {
          label = role ? role.replace(/_/g, ' ').toUpperCase() : 'Unknown';
          color = 'default';
        }
        
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Locations',
      key: 'locations',
      render: (_, record) => getUserLocationsLabel(record),
    },
    {
      title: 'Geotab Databases',
      key: 'geotab_databases',
      render: (_, record) => {
        // Find all Geotab accounts assigned to this user
        const userGeotabAccounts = geotabAccounts.filter(acc => Number(acc.user_id) === Number(record.id));
        if (userGeotabAccounts.length > 0) {
          return (
            <Space wrap>
              {userGeotabAccounts.map(acc => (
                <Tag key={acc.id} color="blue">{acc.database}</Tag>
              ))}
            </Space>
          );
        }
        return <Tag color="default">None</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal('client', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this client?"
            onConfirm={() => handleDelete('client', record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Enterprises table columns
  const enterpriseColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : status === 'inactive' ? 'red' : 'blue'}>
          {String(status || 'unknown').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Geotab Databases',
      key: 'geotab_databases',
      render: (_, record) => {
        // Find all Geotab accounts assigned to this user
        const userGeotabAccounts = geotabAccounts.filter(acc => Number(acc.user_id) === Number(record.id));
        if (userGeotabAccounts.length > 0) {
          return (
            <Space wrap>
              {userGeotabAccounts.map(acc => (
                <Tag key={acc.id} color="blue">{acc.database}</Tag>
              ))}
            </Space>
          );
        }
        return <Tag color="default">None</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal('enterprise', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this enterprise?"
            onConfirm={() => handleDelete('enterprise', record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Geotab Accounts table columns
  const geotabColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Database',
      dataIndex: 'database',
      key: 'database',
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Base URL',
      dataIndex: 'base_url',
      key: 'base_url',
    },
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => {
        if (typeof isActive === 'boolean') {
          return (
            <Tag color={isActive ? 'green' : 'red'}>
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          );
        }
        if (typeof isActive === 'number') {
          return (
            <Tag color={isActive ? 'green' : 'red'}>
              {isActive ? 'Active' : 'Inactive'}
            </Tag>
          );
        }
        const status = record?.status;
        if (typeof status === 'string' && status.length) {
          const s = status.toLowerCase();
          return (
            <Tag color={s === 'active' ? 'green' : s === 'inactive' ? 'red' : 'blue'}>
              {status.toUpperCase()}
            </Tag>
          );
        }
        return <Tag>UNKNOWN</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal('geotab', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this Geotab account?"
            onConfirm={() => handleDelete('geotab', record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Modal form fields based on type
  const renderModalForm = () => {
    if (modalType === 'client') {
      return (
        <>
          <Form.Item name="first_name" label="First Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="last_name" label="Last Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          {!editingRecord && (
            <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
              <Input.Password />
            </Form.Item>
          )}
          {editingRecord && (
            <Form.Item name="password" label="Reset Password (leave blank to keep current)">
              <Input.Password placeholder="Enter new password (optional)" />
            </Form.Item>
          )}
          <Form.Item name="geotab_databases" label="Assign Geotab Databases">
            <Select
              mode="multiple"
              placeholder="Select Geotab databases to assign (optional)"
              allowClear
              showSearch
              optionFilterProp="label"
              options={geotabAccounts.map(acc => ({
                value: acc.id,
                label: `${acc.name || acc.database} (${acc.database}) - ${acc.username}`
              }))}
            />
          </Form.Item>
        </>
      );
    } else if (modalType === 'enterprise') {
      return (
        <>
          <Form.Item name="name" label="Enterprise Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          {!editingRecord && (
            <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
              <Input.Password />
            </Form.Item>
          )}
          {editingRecord && (
            <Form.Item name="password" label="Reset Password (leave blank to keep current)">
              <Input.Password placeholder="Enter new password (optional)" />
            </Form.Item>
          )}
          <Form.Item name="geotab_databases" label="Assign Geotab Databases">
            <Select
              mode="multiple"
              placeholder="Select Geotab databases to assign (optional)"
              allowClear
              showSearch
              optionFilterProp="label"
              options={geotabAccounts.map(acc => ({
                value: acc.id,
                label: `${acc.name || acc.database} (${acc.database}) - ${acc.username}`
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>
        </>
      );
    } else if (modalType === 'geotab') {
      return (
        <>
          <Form.Item name="name" label="Account Name (Optional)">
            <Input placeholder="e.g., Main Fleet Account" />
          </Form.Item>
          
          <Form.Item name="database" label="Database Name" rules={[{ required: true, message: 'Database name is required' }]}>
            <Input placeholder="Enter Geotab database name" />
          </Form.Item>
          
          <Form.Item name="username" label="Geotab Username" rules={[{ required: true, message: 'Username is required' }]}>
            <Input placeholder="Enter Geotab username" />
          </Form.Item>
          
          <Form.Item 
            name="password" 
            label="Geotab Password" 
            rules={[{ required: !editingRecord, message: 'Password is required for new accounts' }]}
          >
            <Input.Password placeholder={editingRecord ? "Leave blank to keep current password" : "Enter Geotab password"} />
          </Form.Item>
          
          <Form.Item name="base_url" label="Base URL" initialValue="https://my.geotab.com">
            <Input placeholder="https://my.geotab.com" />
          </Form.Item>
          
          <Form.Item label="Authentication">
            <Button 
              type="default" 
              onClick={handleTestGeotabConnection}
              loading={testingConnection}
              style={{ marginBottom: 16 }}
            >
              {testingConnection ? 'Authenticating...' : 'Test Connection & Get Session ID'}
            </Button>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
              Click to authenticate with Geotab and retrieve a session ID
            </div>
          </Form.Item>
          
          <Form.Item name="session_id" label="Session ID">
            <Input.TextArea 
              rows={2} 
              placeholder="Click 'Test Connection' to authenticate and get session ID"
              disabled={testingConnection}
            />
          </Form.Item>
          
          <Form.Item name="user_id" label="Assign to User ID (Optional)">
            <Input type="number" placeholder="Leave empty for unassigned account" />
          </Form.Item>
        </>
      );
    } else if (modalType === 'assign-geotab') {
      const unassignedAccounts = geotabAccounts.filter(acc => !acc.user_id || acc.user_id === null);
      const uniqueDatabases = [...new Set(geotabAccounts.map(acc => acc.database).filter(Boolean))];
      
      return (
        <>
          <p style={{ marginBottom: 16 }}>
            Assigning Geotab database to: <strong>{editingRecord?.user_name}</strong>
          </p>
          
          {unassignedAccounts.length > 0 && (
            <Form.Item name="existing_account_id" label="Select Existing Account (Optional)">
              <Select 
                placeholder="Select an existing Geotab account or create new below"
                allowClear
                onChange={(value) => {
                  if (value) {
                    const account = geotabAccounts.find(acc => acc.id === value);
                    if (account) {
                      form.setFieldsValue({
                        database: account.database,
                        username: account.username,
                        base_url: account.base_url || 'https://my.geotab.com'
                      });
                    }
                  } else {
                    form.setFieldsValue({
                      database: '',
                      username: '',
                      base_url: 'https://my.geotab.com'
                    });
                  }
                }}
              >
                {unassignedAccounts.map(acc => (
                  <Option key={acc.id} value={acc.id}>
                    {acc.database} ({acc.username})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
          
          <Form.Item name="database" label="Database Name" rules={[{ required: true }]}>
            <Select
              placeholder="Select or type database name"
              showSearch
              allowClear
              mode="tags"
              maxTagCount={1}
              onChange={(value) => {
                if (Array.isArray(value) && value.length > 0) {
                  form.setFieldsValue({ database: value[0] });
                }
              }}
            >
              {uniqueDatabases.map(db => (
                <Option key={db} value={db}>
                  {db}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="username" label="Username" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password" label="Password" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="base_url" label="Base URL" initialValue="https://my.geotab.com">
            <Input placeholder="https://my.geotab.com" />
          </Form.Item>
        </>
      );
    }
  };

  return (
    <div className="database-management-container">
      <div className="page-header">
        <h1>Database Management</h1>
        <p>Manage users, enterprises, and Geotab integrations</p>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} className="database-tabs">
        <TabPane
          tab={
            <span>
              <UserOutlined />
              Clients
            </span>
          }
          key="clients"
        >
          <div className="tab-content">
            <div className="tab-header">
              <h2>All Clients</h2>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal('client')}
              >
                Add Client
              </Button>
            </div>
            <Table
              columns={clientColumns}
              dataSource={clients}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </TabPane>

        <TabPane
          tab={
            <span>
              <ShopOutlined />
              Enterprises
            </span>
          }
          key="enterprises"
        >
          <div className="tab-content">
            <div className="tab-header">
              <h2>All Enterprises</h2>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal('enterprise')}
              >
                Add Enterprise
              </Button>
            </div>
            <Table
              columns={enterpriseColumns}
              dataSource={enterprises}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
            {console.log('Current enterprises state:', enterprises)}
          </div>
        </TabPane>

        <TabPane
          tab={
            <span>
              <DatabaseOutlined />
              Geotab Accounts
            </span>
          }
          key="geotab"
        >
          <div className="tab-content">
            <div className="tab-header">
              <h2>Geotab Integration Accounts</h2>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal('geotab')}
              >
                Add Geotab Account
              </Button>
            </div>
            <Table
              columns={geotabColumns}
              dataSource={geotabAccounts}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </TabPane>
      </Tabs>

      <Modal
        title={
          modalType === 'assign-geotab' 
            ? 'Assign Geotab Database' 
            : `${editingRecord ? 'Edit' : 'Add'} ${modalType === 'client' ? 'Client' : modalType === 'enterprise' ? 'Enterprise' : 'Geotab Account'}`
        }
        open={modalVisible}
        onCancel={closeModal}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {renderModalForm()}
        </Form>
      </Modal>
    </div>
  );
}

export default DatabaseManagement;
